import { useEffect, useMemo, useRef, useState } from 'react';
import * as fmt from '../lib/format.js';
import { useEscape } from '../lib/useEscape.js';
import {
  estimateEnergy,
  mean,
  movingAverage,
  type BodyProfile,
  type DayRow,
  type Entry,
} from '../lib/stats.js';

interface Props {
  /** Série de saisie : l'historique *plus demain*. */
  series: DayRow[];
  profile: BodyProfile;
  index: number;
  /** Index d'aujourd'hui : au-delà, la journée est une préparation. */
  todayIndex: number;
  onNavigate: (index: number) => void;
  onSave: (day: string, values: Omit<Entry, 'day'>) => void;
  onClear: (day: string) => void;
  onClose: () => void;
}

type Draft = { weight: string; steps: string; kcal: string; gym: boolean };

const toDraft = (row: DayRow): Draft => ({
  weight: row.weight == null ? '' : fmt.measuredWeight(row.weight),
  steps: row.steps == null ? '' : String(row.steps),
  kcal: row.kcal == null ? '' : String(row.kcal),
  gym: row.gym === true,
});

/** Accepte la virgule décimale et ignore les espaces des milliers. */
function parseDecimal(text: string): number | null {
  const cleaned = text.replace(/\s/g, '').replace(',', '.');
  if (cleaned === '') return null;
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : null;
}

function parseInteger(text: string): number | null {
  const digits = text.replace(/[^\d]/g, '');
  if (digits === '') return null;
  const value = Number.parseInt(digits, 10);
  return Number.isFinite(value) ? value : null;
}

const readDraft = (draft: Draft) => ({
  weight: parseDecimal(draft.weight),
  steps: parseInteger(draft.steps),
  kcal: parseInteger(draft.kcal),
  // Ouvrir le panneau puis l'enregistrer vaut réponse : la case est écrite
  // explicitement, `false` compris. C'est ce qui distingue plus tard « pas de
  // séance » de « jamais renseigné » dans la régression.
  gym: draft.gym,
});

/**
 * Bornes de plausibilité, plus serrées que celles de l'API. Elles n'existent
 * que pour intercepter la faute de frappe avant l'aller-retour réseau : un 8000
 * tapé dans le champ poids partait jusqu'au serveur pour revenir en erreur.
 */
const LIMITS = {
  weight: { min: 20, max: 400, label: 'Un poids se situe entre 20 et 400 kg.' },
  steps: { min: 0, max: 100_000, label: 'Un compteur de pas plafonne à 100 000.' },
  kcal: { min: 0, max: 20_000, label: 'Un apport quotidien dépasse rarement 20 000 kcal.' },
} as const;

function implausible(values: ReturnType<typeof readDraft>): string | null {
  for (const field of ['weight', 'steps', 'kcal'] as const) {
    const value = values[field];
    const limit = LIMITS[field];
    if (value != null && (value < limit.min || value > limit.max)) return limit.label;
  }
  return null;
}

/** Vrai quand le brouillon diffère de ce qui est déjà en base. */
const changed = (values: ReturnType<typeof readDraft>, row: DayRow): boolean =>
  values.weight !== row.weight ||
  values.steps !== row.steps ||
  values.kcal !== row.kcal ||
  values.gym !== (row.gym ?? false);

export function DayPanel({
  series,
  profile,
  index,
  todayIndex,
  onNavigate,
  onSave,
  onClear,
  onClose,
}: Props) {
  const row = series[index];
  /**
   * Journée pas encore commencée. Les pas, les calories et la séance d'une
   * ligne sont ceux de la veille : ce soir, ils sont déjà connus et c'est
   * demain qu'ils vont. La pesée, elle, n'aura lieu qu'au réveil — son champ
   * reste donc fermé plutôt que d'inviter à inventer un chiffre.
   */
  const ahead = index > todayIndex;
  const [draft, setDraft] = useState<Draft>(() =>
    row ? toDraft(row) : { weight: '', steps: '', kcal: '', gym: false },
  );

  // Le panneau reste monté pendant la navigation d'un jour à l'autre : il faut
  // donc réamorcer le brouillon quand la journée affichée change.
  useEffect(() => {
    if (row) setDraft(toDraft(row));
  }, [row?.day]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chaîne de saisie explicite : au clavier, Entrée descend d'un champ au
  // suivant puis s'arrête sur « Enregistrer ». Sur téléphone, `enterKeyHint`
  // fait afficher « Suivant » puis « OK » par le clavier virtuel.
  const fields = useRef<Array<HTMLInputElement | null>>([]);
  const saveButton = useRef<HTMLButtonElement | null>(null);

  function focusNext(position: number) {
    const next = fields.current[position + 1];
    if (next) next.focus();
    else saveButton.current?.focus();
  }

  /**
   * Tout le contexte se lit à aujourd'hui au plus tard. Une fenêtre finissant
   * dans le futur mélangerait des mesures et des intentions : sur une journée
   * préparée quinze jours à l'avance, la moyenne 7 j n'aurait plus une seule
   * pesée, et le maintien se calculerait sur des apports prévus en face d'un
   * poids inconnu. Le jour J est la dernière date où les deux existent.
   */
  const contextIndex = Math.min(index, todayIndex);

  const average = useMemo(
    () => movingAverage(series, 7)[contextIndex] ?? null,
    [series, contextIndex],
  );
  const energy = useMemo(
    () => estimateEnergy(series, contextIndex, profile),
    [series, contextIndex, profile],
  );

  // Échap se comporte comme le clic sur le fond : on enregistre, puis on ferme.
  // Le hook doit précéder le retour anticipé plus bas, d'où la garde sur `row`.
  useEscape(() => {
    if (!row) onClose();
    else close();
  });

  const context = useMemo(() => {
    if (!row) return [];
    const lastWeek = series.slice(Math.max(0, contextIndex - 6), contextIndex + 1);
    // Le maintien vient du bilan énergétique sur quatre semaines, pas du seul
    // jour affiché : une journée isolée ne dit rien d'une dépense.
    const balance = row.kcal == null ? null : row.kcal - energy.tdee;

    return [
      {
        key: 'Moyenne 7 j',
        value: average == null ? '—' : `${fmt.weight(average)} kg`,
      },
      {
        // Plus informatif que l'écart à la veille : il situe la pesée par
        // rapport à la tendance, donc dit si elle est aberrante ou non.
        key: 'Écart au lissage',
        value:
          average == null || row.weight == null ? '—' : `${fmt.delta(row.weight - average, 2)} kg`,
      },
      {
        key: 'Pas (moy. 7 j)',
        value: fmt.number(mean(lastWeek.map((item) => item.steps))),
      },
      {
        key: energy.source === 'observed' ? 'Maintien observé' : 'Maintien (modèle)',
        value: `${fmt.number(energy.tdee)} kcal`,
      },
      {
        key: 'Balance du jour',
        value: balance == null ? '—' : `${fmt.signedInteger(balance)} kcal`,
      },
    ];
  }, [series, contextIndex, row, average, energy]);

  if (!row) return null;

  const lastWeek = series.slice(Math.max(0, contextIndex - 6), contextIndex + 1);
  const stepsAverage = mean(lastWeek.map((item) => item.steps));
  const kcalAverage = mean(lastWeek.map((item) => item.kcal));

  const bump = (field: 'weight' | 'steps' | 'kcal', step: number) => {
    setDraft((current) => {
      const parsed = field === 'weight' ? parseDecimal(current[field]) : parseInteger(current[field]);
      // Point de départ plausible quand le champ est vide, pour que le premier
      // appui sur « + » ne parte pas de zéro.
      const fallback = field === 'weight' ? 80 : field === 'steps' ? 8000 : 2200;
      const base = parsed ?? fallback;
      // Arrondi au centième : 75,45 + 0,1 donnerait 75,55000000000001.
      const next = Math.max(0, Math.round((base + step) * 100) / 100);
      return {
        ...current,
        [field]: field === 'weight' ? fmt.measuredWeight(next) : String(Math.round(next)),
      };
    });
  };

  const values = readDraft(draft);
  const problem = implausible(values);
  const dirty = changed(values, row);

  /**
   * N'écrit que si le brouillon a bougé et reste plausible. Sans le test, ouvrir
   * puis refermer une journée vide déclenchait un PUT qui supprimait une ligne
   * inexistante.
   */
  const commit = () => {
    if (problem || !dirty) return;
    onSave(row.day, values);
  };

  /** Toute sortie du panneau enregistre d'abord : croix, fond, Échap. */
  const close = () => {
    commit();
    onClose();
  };

  const goTo = (offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= series.length) return;
    commit();
    onNavigate(target);
  };

  /**
   * La veille de demain, c'est aujourd'hui : autant le dire, sinon le soir venu
   * on ne sait plus quelle journée on renseigne. Plus loin dans la quinzaine,
   * cette veille est elle-même à venir et n'a pas de nom — on la date.
   */
  const eve = !ahead
    ? 'veille'
    : index === todayIndex + 1
      ? 'aujourd’hui'
      : (() => {
          const previous = series[index - 1];
          return previous ? fmt.dayMonth(previous.date) : 'veille';
        })();

  const measures = [
    {
      field: 'weight' as const,
      className: '',
      label: 'Poids',
      unit: 'kg',
      step: 0.1,
      inputMode: 'decimal' as const,
      enterKeyHint: 'next' as const,
      disabled: ahead,
      hint: ahead
        ? 'au réveil'
        : row.weight == null
          ? 'pas encore pesé'
          : average == null
            ? 'pas encore de moyenne 7 j'
            : `${fmt.delta(row.weight - average, 2)} kg vs moyenne 7 j`,
    },
    {
      field: 'steps' as const,
      className: 'measure--steps',
      label: `Pas (${eve})`,
      unit: 'pas',
      step: 500,
      inputMode: 'numeric' as const,
      enterKeyHint: 'next' as const,
      disabled: false,
      hint: stepsAverage ? `moyenne 7 j : ${fmt.number(stepsAverage)}` : 'pas de moyenne',
    },
    {
      field: 'kcal' as const,
      className: 'measure--kcal',
      label: `Calories (${eve})`,
      unit: 'kcal',
      step: 50,
      inputMode: 'numeric' as const,
      enterKeyHint: 'done' as const,
      disabled: false,
      hint: kcalAverage ? `moyenne 7 j : ${fmt.number(kcalAverage)}` : 'pas de moyenne',
    },
  ];

  const offsetFromToday = todayIndex - index;

  return (
    <>
      <button type="button" className="overlay" aria-label="Fermer" onClick={close} />
      <aside className="panel" role="dialog" aria-label={`Journée du ${fmt.dayMonth(row.date)}`}>
        <div className="panel__grab" aria-hidden="true" />

        <header className="panel__head">
          <div>
            <div className="eyebrow">{ahead ? 'Préparation' : 'Journée'}</div>
            <h2 className="panel__title">
              {fmt.date(row.date, { weekday: 'short', day: 'numeric', month: 'long' })}
            </h2>
            <p className="panel__rel">{fmt.relativeDays(offsetFromToday)}</p>
          </div>
          <div className="panel__nav">
            <button type="button" className="btn--icon" onClick={() => goTo(-1)} disabled={index === 0} aria-label="Jour précédent">
              ‹
            </button>
            <button
              type="button"
              className="btn--icon"
              onClick={() => goTo(1)}
              disabled={index >= series.length - 1}
              aria-label="Jour suivant"
            >
              ›
            </button>
            <button type="button" className="btn--icon" style={{ marginLeft: 6 }} onClick={close} aria-label="Fermer">
              ✕
            </button>
          </div>
        </header>

        {ahead ? (
          <p className="panel__ahead" role="note">
            Une journée porte les mesures de sa <b>veille</b> : ici, les pas, les calories
            et la séance {index === todayIndex + 1 ? 'd’' : 'du '}
            <b>{eve}</b>, en face de la pesée du {fmt.dayMonth(row.date)} au matin. Rien de
            tout cela n’entre dans les moyennes ni dans la tendance avant le jour venu.
          </p>
        ) : null}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {measures.map((measure, position) => (
            <div className={`measure ${measure.className}`} key={measure.field}>
              <div className="measure__meta">
                <label className="measure__label" htmlFor={`measure-${measure.field}`}>
                  {measure.label}
                </label>
                <span className="measure__hint">{measure.hint}</span>
              </div>
              <input
                id={`measure-${measure.field}`}
                ref={(node) => {
                  fields.current[position] = node;
                }}
                value={draft[measure.field]}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, [measure.field]: event.target.value }))
                }
                onKeyDown={(event) => {
                  // `isComposing` : sur un clavier à composition, Entrée valide
                  // la saisie en cours et ne doit pas changer de champ.
                  if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
                  event.preventDefault();
                  focusNext(position);
                }}
                inputMode={measure.inputMode}
                enterKeyHint={measure.enterKeyHint}
                disabled={measure.disabled}
                placeholder="—"
                aria-label={`${measure.label} en ${measure.unit}`}
              />
              <div className="measure__unit">{measure.unit}</div>
              <div className="measure__steppers">
                <button
                  type="button"
                  disabled={measure.disabled}
                  onClick={() => bump(measure.field, measure.step)}
                  aria-label={`Augmenter ${measure.label}`}
                >
                  +
                </button>
                <button
                  type="button"
                  disabled={measure.disabled}
                  onClick={() => bump(measure.field, -measure.step)}
                  aria-label={`Diminuer ${measure.label}`}
                >
                  −
                </button>
              </div>
            </div>
          ))}
        </div>

        <label className="measure measure--flag">
          <input
            type="checkbox"
            checked={draft.gym}
            onChange={(event) =>
              setDraft((current) => ({ ...current, gym: event.target.checked }))
            }
          />
          <div className="measure__meta">
            <span className="measure__label">Séance ({eve})</span>
            <span className="measure__hint">
              Renforcement : la pesée du lendemain en garde la trace jusqu’à 72 h
            </span>
          </div>
        </label>

        <div className="context">
          <div className="stat__label">
            {ahead ? 'Contexte — arrêté à aujourd’hui' : 'Contexte'}
          </div>
          {context.map((item) => (
            <div className="context__row" key={item.key}>
              <span>{item.key}</span>
              <b>{item.value}</b>
            </div>
          ))}
        </div>

        {problem ? (
          <p className="error" role="alert" style={{ marginTop: 12 }}>
            {problem}
          </p>
        ) : null}

        <div className="panel__actions">
          <button
            ref={saveButton}
            type="button"
            className="btn"
            disabled={problem !== null}
            onClick={close}
          >
            Enregistrer
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setDraft({ weight: '', steps: '', kcal: '', gym: false });
              onClear(row.day);
            }}
          >
            Effacer
          </button>
        </div>
      </aside>
    </>
  );
}
